import { useState } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import {
  AccessibleSelect,
  Button,
  DrawerBody,
  DrawerFooter,
  InputControl,
  Label,
  RadioButton,
  RadioGroup,
  TextArea,
  cn,
  useContentMask,
} from '@growtherapy/sprout-ui';

import {
  MAX_CHARACTER_COUNT,
  SessionIntentionNotificationCadence,
  SessionIntentionNotificationTimeOfDay,
  validateIntentionText,
} from '@grow-therapy-team/seedling-components';

import { SessionIntention } from '../../hooks/intentions/types';
import { CurrentSessionIntention } from '../../hooks/intentions/useGetCurrentIntentionQuery';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';

const NO_REMINDERS_VALUE = 'no reminders';

function TimeOfDayRadioButton({ value }: { value: SessionIntentionNotificationTimeOfDay }) {
  const t = useTranslate();
  const { helperText, label } = (() => {
    switch (value) {
      case SessionIntentionNotificationTimeOfDay.MORNING:
        return {
          helperText: t('telehealth-core:intentions.around9am'),
          label: t('telehealth-core:intentions.morning'),
        };
      case SessionIntentionNotificationTimeOfDay.AFTERNOON:
        return {
          helperText: t('telehealth-core:intentions.around1pm'),
          label: t('telehealth-core:intentions.afternoon'),
        };
      case SessionIntentionNotificationTimeOfDay.EVENING:
      default:
        return {
          helperText: t('telehealth-core:intentions.around6pm'),
          label: t('telehealth-core:intentions.evening'),
        };
    }
  })();

  return (
    <RadioButton
      label={
        <div className='flex flex-col gap-100'>
          <Label.Small emphasis>{label}</Label.Small>
          <Label.Xsmall className='text-text-muted'>{helperText}</Label.Xsmall>
        </div>
      }
      value={value}
      className='w-full '
    />
  );
}

export function SetIntentionDrawerBody({
  isDarkTheme,
  onClickBack,
  onClickNext,
  loading,
  intention,
  appointmentShortId,
}: {
  isDarkTheme: boolean;
  loading: boolean;
  intention: SessionIntention | CurrentSessionIntention | null;
  appointmentShortId: string | undefined;
  onClickBack: () => void;
  onClickNext: (
    intention: string,
    notificationCadence: SessionIntentionNotificationCadence | undefined,
    notificationTimeOfDay: SessionIntentionNotificationTimeOfDay | undefined,
  ) => Promise<void>;
}) {
  const { maskClassName, maskProps } = useContentMask({ mask: 'exclude' });
  const t = useTranslate();
  const { userClicked } = useTrackEvent();
  const [error, setError] = useState<string | null>(null);
  const [intentionText, setIntentionText] = useState(intention?.contentText ?? '');
  const [notificationCadence, setNotificationCadence] = useState<SessionIntentionNotificationCadence | null>(
    intention ? intention.notificationCadence : SessionIntentionNotificationCadence.DAILY,
  );
  const [notificationTimeOfDay, setNotificationTimeOfDay] = useState<SessionIntentionNotificationTimeOfDay>(
    intention?.notificationTimeOfDay ?? SessionIntentionNotificationTimeOfDay.MORNING,
  );

  const onClick = () => {
    const trimmedIntention = intentionText.trim();
    const error = validateIntentionText(t, trimmedIntention);

    if (error) {
      setError(error);
      return;
    }

    userClicked(EventTypeEnum.INTENTION_SUBMITTED, {
      appointmentShortId: appointmentShortId ?? '',
    });

    onClickNext(
      intentionText.trim(),
      notificationCadence ?? undefined,
      notificationCadence ? notificationTimeOfDay : undefined,
    );
  };

  return (
    <>
      <DrawerBody className='flex flex-col p-350 rebrand:pt-300'>
        <InputControl
          label={
            intention
              ? t('telehealth-core:intentions.editYourIntention')
              : t('telehealth-core:intentions.setYourIntention')
          }
          wordCount={{ max: MAX_CHARACTER_COUNT, currentLength: intentionText.length }}
          error={error}
          inverse={isDarkTheme}
        >
          <TextArea
            placeholder={t('telehealth-core:intentions.whatDoYouWantToFocusOn')}
            className={cn(maskClassName, 'w-full')}
            value={intentionText}
            onChange={(e) => {
              setIntentionText(e.target.value);
              setError(null);
            }}
            autoSize
            {...maskProps}
          />
        </InputControl>
        <InputControl
          label={t('telehealth-core:intentions.reminderFrequency')}
          description={
            notificationCadence && t('telehealth-core:intentions.youllGetANotificationRemindingYouAboutYourIntention')
          }
          inverse={isDarkTheme}
        >
          <AccessibleSelect
            onChange={(value): void => {
              setNotificationCadence(
                value === NO_REMINDERS_VALUE ? null : (value as SessionIntentionNotificationCadence),
              );
            }}
            options={[
              {
                label: t('telehealth-core:intentions.daily'),
                value: SessionIntentionNotificationCadence.DAILY,
              },
              {
                label: t('telehealth-core:intentions.everyOtherDay'),
                value: SessionIntentionNotificationCadence.EVERY_OTHER_DAY,
              },
              {
                label: t('telehealth-core:intentions.weekly'),
                value: SessionIntentionNotificationCadence.WEEKLY,
              },
              {
                label: t('telehealth-core:intentions.noReminders'),
                value: NO_REMINDERS_VALUE,
              },
            ]}
            value={notificationCadence ?? NO_REMINDERS_VALUE}
          />
        </InputControl>
        {notificationCadence && (
          <InputControl label={t('telehealth-core:intentions.timeOfDay')} inverse={isDarkTheme}>
            <RadioGroup
              onChange={(e): void => {
                setNotificationTimeOfDay(e.target.value as SessionIntentionNotificationTimeOfDay);
              }}
              name='time-of-day'
              value={notificationTimeOfDay}
              className='flex-col rebrand:gap-150'
            >
              <TimeOfDayRadioButton value={SessionIntentionNotificationTimeOfDay.MORNING} />
              <TimeOfDayRadioButton value={SessionIntentionNotificationTimeOfDay.AFTERNOON} />
              <TimeOfDayRadioButton value={SessionIntentionNotificationTimeOfDay.EVENING} />
            </RadioGroup>
          </InputControl>
        )}
      </DrawerBody>
      <DrawerFooter className='justify-end rebrand:gap-100'>
        {intention && (
          <Button use='secondary' onClick={onClickBack}>
            {t('telehealth-core:core.cancel')}
          </Button>
        )}
        <Button onClick={onClick} loading={loading}>
          {t('telehealth-core:intentions.setIntention')}
        </Button>
      </DrawerFooter>
    </>
  );
}
